import { lumi } from "@/lib/lumi"
import { useCallback, useEffect, useState } from "react"
import toast from "react-hot-toast"

export type MoldShape = "rectangular" | "round" | "slab"

export interface MoldPreset {
  _id?: string
  name: string
  shape: MoldShape
  length: number
  width: number
  height: number
  diameter: number
  unit: "grams" | "ounces"
  userId?: string
  creator?: string
  createdAt?: string
  updatedAt?: string
}

export type MoldDimensions = Pick<
  MoldPreset,
  "name" | "shape" | "length" | "width" | "height" | "diameter" | "unit"
>

/**
 * Mold dimension presets saved to the signed-in user's profile.
 * Loading is skipped entirely when nobody is signed in, since the
 * collection is readable only by its creator.
 */
export function useMoldPresets(enabled: boolean) {
  const [presets, setPresets] = useState<MoldPreset[]>([])
  const [userId, setUserId] = useState<string | null>(lumi.auth.user?.userId ?? null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setUserId(lumi.auth.user?.userId ?? null)
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUserId(user?.userId ?? null)
    })
    return () => unsubscribe()
  }, [])

  const load = useCallback(async () => {
    if (!enabled) {
      setPresets([])
      return
    }
    setLoading(true)
    try {
      const { list } = await lumi.entities.mold_presets.list({
        sort: { name: 1 },
        limit: 50,
      })
      setPresets(list as MoldPreset[])
    } catch (error) {
      console.error("Failed to load mold presets:", error)
      toast.error("Could not load your saved molds")
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    void load()
  }, [load, userId])

  const savePreset = useCallback(
    async (dimensions: MoldDimensions) => {
      if (!userId) {
        toast.error("Sign in to save mold presets")
        return false
      }
      setSaving(true)
      try {
        const now = new Date().toISOString()
        const existing = presets.find(
          (p) => p.name.trim().toLowerCase() === dimensions.name.trim().toLowerCase()
        )

        if (existing?._id) {
          await lumi.entities.mold_presets.update(existing._id, {
            ...dimensions,
            updatedAt: now,
          })
          toast.success(`Updated "${dimensions.name}"`)
        } else {
          await lumi.entities.mold_presets.create({
            ...dimensions,
            userId,
            creator: userId,
            createdAt: now,
            updatedAt: now,
          })
          toast.success(`Saved "${dimensions.name}"`)
        }
        await load()
        return true
      } catch (error) {
        console.error("Failed to save mold preset:", error)
        toast.error("Could not save this mold")
        return false
      } finally {
        setSaving(false)
      }
    },
    [userId, presets, load]
  )

  const deletePreset = useCallback(
    async (id: string) => {
      try {
        await lumi.entities.mold_presets.delete(id)
        setPresets((prev) => prev.filter((p) => p._id !== id))
        toast.success("Mold preset deleted")
      } catch (error) {
        console.error("Failed to delete mold preset:", error)
        toast.error("Could not delete this mold")
      }
    },
    []
  )

  return { presets, loading, saving, load, savePreset, deletePreset, canSave: Boolean(userId) }
}
